import { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  MapPin,
  Phone,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { siteContent } from "@/content/site";

const { contact } = siteContent;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", message: contact.errors.missingFields });
      return;
    }
    setStatus({ type: "loading", message: "" });
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      setStatus({ type: "success", message: contact.success });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || contact.errors.fallback;
      setStatus({ type: "error", message: detail });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 lg:py-28 bg-[#E1EEEA]/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8A33D]">
              {contact.eyebrow}
            </span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A36] leading-tight">
              {contact.headingLead}{" "}
              <em className="italic font-medium text-[#1F4E5F]">
                {contact.headingItalic}
              </em>
            </h2>
            <p className="mt-4 text-[#3D5560] text-lg">{contact.body}</p>

            <div className="mt-8 space-y-4">
              <ContactDetail
                icon={Mail}
                label={contact.details.emailLabel}
                value={contact.details.email}
              />
              <ContactDetail
                icon={Phone}
                label={contact.details.phoneLabel}
                value={contact.details.phone}
              />
              <ContactDetail
                icon={MapPin}
                label={contact.details.addressLabel}
                value={contact.details.address}
              />
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white border border-[#C9DDD6] rounded-3xl p-7 sm:p-9 lg:p-10 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={contact.fields.nameLabel} id="c-name">
                <input
                  id="c-name"
                  type="text"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder={contact.fields.namePlaceholder}
                  className="w-full bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
                />
              </Field>
              <Field label={contact.fields.emailLabel} id="c-email">
                <input
                  id="c-email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder={contact.fields.emailPlaceholder}
                  className="w-full bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
                />
              </Field>
            </div>
            <Field label={contact.fields.subjectLabel} id="c-subject">
              <input
                id="c-subject"
                type="text"
                data-testid="contact-input-subject"
                value={form.subject}
                onChange={(e) => update("subject", e.target.value)}
                placeholder={contact.fields.subjectPlaceholder}
                className="w-full bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
              />
            </Field>
            <Field label={contact.fields.messageLabel} id="c-message">
              <textarea
                id="c-message"
                rows={5}
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder={contact.fields.messagePlaceholder}
                className="w-full bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition resize-none"
              />
            </Field>

            {status.type === "error" && (
              <div
                data-testid="contact-error"
                className="flex items-center gap-2 text-[#C88A28] bg-[#E8A33D]/10 border border-[#E8A33D]/25 rounded-xl px-3 py-2.5 text-sm"
              >
                <AlertCircle className="w-4 h-4" />
                <span>{status.message}</span>
              </div>
            )}
            {status.type === "success" && (
              <div
                data-testid="contact-success"
                className="flex items-center gap-2 text-[#2D7A4A] bg-[#5BAE71]/15 border border-[#5BAE71]/30 rounded-xl px-3 py-2.5 text-sm"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{status.message}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={status.type === "loading"}
              className="inline-flex items-center justify-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-7 py-3.5 rounded-full text-[15px] font-semibold transition-all hover:gap-3 disabled:opacity-70"
            >
              {status.type === "loading" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> {contact.submitting}
                </>
              ) : (
                <>
                  {contact.submit} <Send className="w-4 h-4" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold uppercase tracking-[0.14em] text-[#3D5560] mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ContactDetail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 rounded-2xl bg-[#1F4E5F] text-[#F4FAF8] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#6A8290]">
          {label}
        </p>
        <p className="text-[#0E2A36] font-medium">{value}</p>
      </div>
    </div>
  );
}
